<template>
  <modal
    v-if="show"
    width="560px"
    class="update-billing"
    bodyOverflow="auto"
    @close="close"
  >
    <template #header>
      <ModalHeader @close="$emit('close')">
        <template #header>
          <div class="dialog-title-text">{{ t('Billing Info') }}</div>
          <header-buttons />
          <button class="dialog-close-button" @click="close">
            <ui-icon size="18" :color="$colors.white" icon="fas fa-times" />
          </button>
        </template>
      </ModalHeader>
    </template>
    <div class="billing-wrap__input">
      <div class="row-2-elements">
        <ui-input
          v-model="billingInfo.first_name"
          :label="t('First name')"
          :placeholder="t('First name')"
          name="first_name"
          autofocus
          :error-messages="errorsDict['first_name']"
        />
        <ui-input
          v-model="billingInfo.last_name"
          :label="t('Last name')"
          :placeholder="t('Last name')"
          name="last_name"
          :error-messages="errorsDict['last_name']"
        />
      </div>
      <div class="row-2-elements">
        <ui-input
          v-model="billingInfo.phone"
          :label="t('Phone number')"
          :placeholder="t('Phone number')"
          name="phone"
          :error-messages="errorsDict['phone']"
        />
        <ui-input
          v-model="billingInfo.company"
          :label="t('Company')"
          :placeholder="t('Company')"
          name="company"
          :error-messages="errorsDict['company']"
        />
      </div>
      <div>
        <ui-input
          v-model="billingInfo.line1"
          :label="t('Address line 1')"
          :placeholder="t('Address line 1')"
          name="line1"
          :error-messages="errorsDict['line1']"
        />
      </div>
      <div>
        <ui-input
          v-model="billingInfo.line2"
          :label="t('Address line 2')"
          :placeholder="t('Address line 2')"
          name="line2"
          :error-messages="errorsDict['line2']"
        />
      </div>
      <div class="row-2-elements">
        <ui-select
          v-model="billingInfo.country"
          :items="store.countryList"
          :placeholder="t('Country')"
          :label="`${t('Country')}*`"
          item-title="name"
          item-value="code"
          name="country"
          :return-object="false"
          :error-messages="errorsDict['country']"
          max-width-calc
        />
        <ui-input
          v-model="billingInfo.vat"
          :label="t('VAT')"
          :placeholder="t('VAT')"
          name="vat"
          :error-messages="errorsDict['vat']"
        />
      </div>
      <div class="row-2-elements">
        <ui-input
          v-model="billingInfo.state"
          :label="t('State')"
          :placeholder="t('State')"
          name="state"
          :error-messages="errorsDict['state']"
        />
        <ui-input
          v-model="billingInfo.city"
          :label="t('City')"
          :placeholder="t('City')"
          name="city"
          :error-messages="errorsDict['city']"
        />
      </div>
      <div class="row-2-elements">
        <ui-input
          v-model="billingInfo.zip"
          :label="t('ZIP / Postal Code')"
          :placeholder="t('ZIP / Postal Code')"
          name="zip"
          :error-messages="errorsDict['zip']"
        />
        <div></div>
      </div>
    </div>

    <template #footer>
      <div class="d-flex align-items-center justify-content-between">
        <error-aggregator
          class="aggregator-wrap"
          v-model="errors"
          v-model:active="errorsControlActive"
        />

        <div class="d-flex">
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="loading"
            @click="close"
          />

          <ui-button
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            :loading="loading"
            @click="save"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import HeaderButtons from '@/components/ui/ui-modal/modal/header-buttons.vue';
import {
  getBillingCustomerApiRequest,
  postUpdateBillingInfoApiRequest,
} from '@/services/api/billing-api';
import ModalHeader from '@/components/ui/ui-modal/modal/header.vue';
import { useStore } from '@/store';
import { useApiFieldErrors, useColors, useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';

type BillingInfo = Record<string, any> & { billing_address?: Record<string, any> };

defineProps<{
  data?: any;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'success'): void;
}>();

const { t } = useI18n();
const $colors = useColors();
const store = useStore();
const {
  fieldErrors,
  errors,
  errorsControlActive,
  errorsDict,
  clearErrors,
  applyApiErrors,
} = useApiFieldErrors();

const show = ref(false);
const loading = ref(false);
const billingInfo = ref<BillingInfo>({
  billing_address: {},
});

const load = async () => {
  store.setLoading(true);
  loading.value = true;
  try {
    const customer = (await getBillingCustomerApiRequest()) as any;
    billingInfo.value = customer.billingInfo as BillingInfo;
    store.setBillingCustomer(customer);
    show.value = true;
  } finally {
    loading.value = false;
    store.setLoading(false);
  }
};

const close = () => {
  emit('close');
};

const save = async () => {
  clearErrors();
  store.setLoading(true);
  loading.value = true;
  try {
    await postUpdateBillingInfoApiRequest(billingInfo.value);
    emit('close');
    $emitter.emit('toast-success', t('Billing info successfully changed.'));
  } catch (error: any) {
    applyApiErrors(error, { nested: true });
  } finally {
    loading.value = false;
    store.setLoading(false);
  }
};

onMounted(() => {
  if (loading.value) return;
  load();
});
</script>

<style lang="scss" scoped>
.row-2-elements {
  display: flex;
  column-gap: 16px;
  & > div {
    flex: 1 0 0;
  }
}
</style>

<style lang="scss">
.update-billing-info {
  height: 100%;
  max-height: 700px !important;
}
</style>
